import { TenantApi } from '@liks/lac-svc-api/esm/apis/TenantApi';
import type { Tenant } from '@liks/lac-svc-api/esm/models/index';
import type { LacCreateTenantInput, LacUpdateTenantInput } from '../types/lac';
import { lacApiConfiguration } from './lacApi';

export type { LacCreateTenantInput, LacUpdateTenantInput } from '../types/lac';

const PAGE_SIZE = 100;

function compareLocale(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

export function tenantDisplayName(tenant: Tenant): string {
  return tenant.displayName?.trim() || tenant.name;
}

class LacTenantService {
  private readonly tenantApi = new TenantApi(lacApiConfiguration);

  async listAllTenants(): Promise<Tenant[]> {
    const tenants: Tenant[] = [];
    let offset = 0;

    for (;;) {
      const page =
        await this.tenantApi.deLiksTenantBoundaryTenantsResourceListTenants({
          offset,
          limit: PAGE_SIZE,
        });

      tenants.push(...page);

      if (page.length < PAGE_SIZE) {
        break;
      }

      offset += PAGE_SIZE;
    }

    return tenants;
  }

  async listTenants(): Promise<Tenant[]> {
    const tenants = await this.listAllTenants();

    return tenants.sort((a, b) =>
      compareLocale(tenantDisplayName(a), tenantDisplayName(b)),
    );
  }

  async createTenant(input: LacCreateTenantInput): Promise<Tenant> {
    const trimmedName = input.name.trim();
    const trimmedDisplayName = input.displayName?.trim() || trimmedName;
    const trimmedDescription = input.description?.trim();

    return this.tenantApi.deLiksTenantBoundaryTenantsResourceCreateTenant({
      tenant: {
        name: trimmedName,
        displayName: trimmedDisplayName,
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
        ...(input.maxUsers != null ? { maxUsers: input.maxUsers } : {}),
        ...(input.maxChatbots != null
          ? { maxChatbots: input.maxChatbots }
          : {}),
        ...(input.active != null ? { active: input.active } : {}),
      },
    });
  }

  async getTenant(tenantName: string): Promise<Tenant> {
    return this.tenantApi.deLiksTenantBoundaryTenantsResourceReadTenant({
      tenantName: tenantName.trim(),
    });
  }

  async updateTenant(input: LacUpdateTenantInput): Promise<Tenant> {
    const tenantName = input.tenantName.trim();
    const displayName = input.displayName.trim();
    const description = input.description?.trim() ?? '';

    const body: Record<string, unknown> = {
      name: tenantName,
      displayName,
      description: description === '' ? null : description,
      maxUsers: input.maxUsers ?? null,
      maxChatbots: input.maxChatbots ?? null,
      active: input.active ?? null,
    };

    if (input.logo !== undefined) {
      body.logo = input.logo === '' || input.logo == null ? null : input.logo;
    }

    return this.tenantApi.deLiksTenantBoundaryTenantsResourceUpdateTenant({
      tenantName,
      body: JSON.stringify(body),
    });
  }

  async setTenantActive(tenantName: string, active: boolean): Promise<Tenant> {
    return this.tenantApi.deLiksTenantBoundaryTenantsResourceSetTenantActive({
      tenantName: tenantName.trim(),
      body: active,
    });
  }

  async deleteTenant(tenantName: string): Promise<void> {
    await this.tenantApi.deLiksTenantBoundaryTenantsResourceDeleteTenant({
      tenantName: tenantName.trim(),
    });
  }
}

export const lacTenantService = new LacTenantService();
